import { __decorate } from "tslib";
import { property, state } from 'lit/decorators.js';
import { consumeContext, watchContext } from '../../base/context';
import { eventListener } from '../../base/events';
import { mediaContext, MediaRemoteControl } from '../../media';
import { setAttribute } from '../../utils/dom';
import { round } from '../../utils/number';
import { SliderElement } from '../slider';
export const VOLUME_SLIDER_ELEMENT_TAG_NAME = 'vds-volume-slider';
/**
 * A slider control that lets the user specify their desired volume level.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `media-can-play`: Applied when media can begin playback.
 *
 * @tagname vds-volume-slider
 *  @example
 * ```html
 * <vds-volume-slider
 *   label="Media volume slider"
 * ></vds-volume-slider>
 * ```
 * @example
 * ```css
 * vds-volume-slider {
 *   --vds-slider-track-height: 2.5px;
 *   --vds-slider-thumb-width: 16px;
 *   --vds-slider-thumb-height: 16px;
 *   --vds-slider-active-color: #ff2a5d;
 * }
 * ```
 */
export class VolumeSliderElement extends SliderElement {
    constructor() {
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        super(...arguments);
        this.label = 'Media volume slider';
        this.step = 0.5;
        this.keyboardStep = 0.5;
        this.shiftKeyMultiplier = 10;
        /** @internal */
        this.min = 0;
        /** @internal */
        this.max = 100;
        this._mediaVolume = mediaContext.volume.initialValue;
        /**
         * Represents the current volume out of 100.
         *
         * @internal
         */
        this.value = this._mediaVolume * 100;
        this._mediaRemote = new MediaRemoteControl(this);
    }
    /**
     * The current media volume level (between 0 - 1).
     */
    get volume() {
        return round(this.value / 100, 3);
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    update(changedProperties) {
        if (changedProperties.has('_mediaVolume')) {
            this.value = this._mediaVolume * 100;
        }
        super.update(changedProperties);
    }
    // -------------------------------------------------------------------------------------------
    // Methods
    // -------------------------------------------------------------------------------------------
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _handleSliderValueChange(event) {
        const newVolume = event.detail;
        const mediaVolume = round(newVolume / 100, 3);
        this._mediaRemote.changeVolume(mediaVolume, event);
    }
}
__decorate([
    property({ attribute: false })
], VolumeSliderElement.prototype, "min", void 0);
__decorate([
    property({ attribute: false })
], VolumeSliderElement.prototype, "max", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.volume)
], VolumeSliderElement.prototype, "_mediaVolume", void 0);
__decorate([
    property({ attribute: false, state: true })
], VolumeSliderElement.prototype, "value", void 0);
__decorate([
    watchContext(mediaContext.canPlay)
], VolumeSliderElement.prototype, "_handleCanPlayContextUpdate", null);
__decorate([
    eventListener('vds-slider-value-change')
], VolumeSliderElement.prototype, "_handleSliderValueChange", null);
